import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Check, ChevronDown, ChevronUp, Lightbulb, ListChecks, X } from "lucide-react";
import { getAuthCookie } from "../../utils/authCookies";
import {
  activeStep,
  loadProgress,
  longestMatchLength,
  onboardingSteps,
  progressCounts,
  saveProgress,
  type OnboardingAudience,
  type OnboardingProgress,
  type OnboardingStep,
} from "./onboardingGuide";
import "./SetupGuide.css";

type Props = {
  audience: OnboardingAudience;
};

function userKey() {
  return getAuthCookie("globalUserId") || getAuthCookie("email") || "local";
}

export default function BrokerageOnboarding({ audience }: Props) {
  const steps = onboardingSteps[audience];
  const location = useLocation();
  const navigate = useNavigate();
  const key = userKey();
  const [progress, setProgress] = useState<OnboardingProgress>(() => loadProgress(key));
  const [showLater, setShowLater] = useState(false);

  useEffect(() => {
    saveProgress(key, progress);
  }, [key, progress]);

  const visitStep = useMemo(
    () => steps.find((step) => step.autoDoneOnVisit && longestMatchLength(location.pathname, step) > 0),
    [location.pathname, steps]
  );

  useEffect(() => {
    if (!visitStep) return;
    setProgress((current) => {
      if (current.done[visitStep.id] !== undefined) return current;
      return { ...current, done: { ...current.done, [visitStep.id]: true } };
    });
  }, [visitStep, progress.done]);

  const counts = progressCounts(steps, progress.done);
  const finished = counts.total > 0 && counts.complete === counts.total;
  const hint = activeStep(location.pathname, steps, progress.done, progress.dismissedHints);
  const setupSteps = steps.filter((step) => !step.later);
  const laterSteps = steps.filter((step) => step.later);
  const percent = counts.total ? Math.round((counts.complete / counts.total) * 100) : 0;

  const patch = (next: Partial<OnboardingProgress>) => {
    setProgress((current) => ({ ...current, ...next }));
  };

  const toggleDone = (step: OnboardingStep) => {
    setProgress((current) => {
      const willBeDone = !current.done[step.id];
      const dismissedHints = { ...current.dismissedHints };
      if (!willBeDone) delete dismissedHints[step.id];
      return {
        ...current,
        done: { ...current.done, [step.id]: willBeDone },
        dismissedHints,
      };
    });
  };

  const dismissHint = (stepId: string) => {
    setProgress((current) => ({
      ...current,
      dismissedHints: { ...current.dismissedHints, [stepId]: true },
    }));
  };

  const openGuide = () => patch({ hidden: false, collapsed: false });

  const go = (step: OnboardingStep) => {
    if (location.pathname !== step.path) navigate(step.path);
  };

  const hintCard = hint && (progress.collapsed || progress.hidden) ? (
    <div className="re-onb-hint" role="status">
      <div className="re-onb-hint-kicker">
        <Lightbulb size={14} />
        Tip for this page
      </div>
      <h3>{hint.title}</h3>
      <p>{hint.hint}</p>
      <div className="re-onb-hint-actions">
        <button type="button" className="re-onb-textbtn primary" onClick={() => toggleDone(hint)}>
          Mark done
        </button>
        <button type="button" className="re-onb-textbtn" onClick={() => dismissHint(hint.id)}>
          Hide tip
        </button>
      </div>
    </div>
  ) : null;

  if (progress.hidden) {
    return (
      <div className="re-onb">
        {hintCard}
        <button type="button" className="re-onb-launcher" onClick={openGuide}>
          <span className="re-onb-launcher-icon">
            <ListChecks size={16} />
          </span>
          <span className="re-onb-launcher-copy">
            <strong>Setup guide</strong>
            <span>{finished ? "Complete" : `${counts.complete} of ${counts.total}`}</span>
          </span>
        </button>
      </div>
    );
  }

  if (progress.collapsed) {
    return (
      <div className="re-onb">
        {hintCard}
        <button type="button" className="re-onb-launcher" onClick={() => patch({ collapsed: false })}>
          <span className="re-onb-launcher-icon">
            <ListChecks size={16} />
          </span>
          <span className="re-onb-launcher-copy">
            <strong>{finished ? "You are set up" : "Setup guide"}</strong>
            <span>{finished ? "All required steps done" : `${counts.complete} of ${counts.total} done`}</span>
          </span>
        </button>
      </div>
    );
  }

  return (
    <div className="re-onb">
      <section className="re-onb-card" aria-label="Brokerage setup guide">
        <header className="re-onb-card-head">
          <div>
            <h2>{audience === "firm" ? "Set up your brokerage" : "Your first tasks"}</h2>
            <p>
              {audience === "firm"
                ? "Do these in order. You can leave and come back."
                : "Your menu only shows what your role allows."}
            </p>
          </div>
          <button type="button" className="re-onb-iconbtn" aria-label="Collapse setup guide" onClick={() => patch({ collapsed: true })}>
            <X size={16} />
          </button>
        </header>

        <div className="re-onb-progress">
          <div className="re-onb-progress-label">
            <span>{finished ? "Required steps complete" : "Required steps"}</span>
            <span>{counts.complete} of {counts.total}</span>
          </div>
          <div className="re-onb-track" aria-hidden="true">
            <span style={{ width: `${percent}%` }} />
          </div>
        </div>

        {finished && (
          <div className="re-onb-success">
            <strong>You are set up.</strong>
            <p>Login still opens Welcome. Pick today’s job from the menu.</p>
          </div>
        )}

        {hint && (
          <div className="re-onb-hint" style={{ margin: "0 14px 8px" }} role="status">
            <div className="re-onb-hint-kicker">
              <Lightbulb size={14} />
              Tip for this page
            </div>
            <p>{hint.hint}</p>
          </div>
        )}

        <ul className="re-onb-list">
          {setupSteps.map((step) => (
            <StepRow
              key={step.id}
              step={step}
              done={Boolean(progress.done[step.id])}
              current={longestMatchLength(location.pathname, step) > 0}
              onToggle={() => toggleDone(step)}
              onGo={() => go(step)}
            />
          ))}
        </ul>

        {laterSteps.length > 0 && (
          <>
            <button type="button" className="re-onb-later-toggle" onClick={() => setShowLater((open) => !open)}>
              {showLater ? <ChevronUp size={14} style={{ marginRight: 6 }} /> : <ChevronDown size={14} style={{ marginRight: 6 }} />}
              Later, not the first week
            </button>
            {showLater && (
              <ul className="re-onb-list">
                {laterSteps.map((step) => (
                  <StepRow
                    key={step.id}
                    step={step}
                    done={Boolean(progress.done[step.id])}
                    current={longestMatchLength(location.pathname, step) > 0}
                    onToggle={() => toggleDone(step)}
                    onGo={() => go(step)}
                  />
                ))}
              </ul>
            )}
          </>
        )}

        <div className="re-onb-card-foot">
          <button type="button" className="re-onb-textbtn" onClick={() => patch({ hidden: true })}>
            Hide guide
          </button>
          <button
            type="button"
            className="re-onb-textbtn"
            onClick={() => patch({ done: {}, dismissedHints: {}, collapsed: false, hidden: false })}
          >
            Start over
          </button>
        </div>
      </section>
    </div>
  );
}

function StepRow({
  step,
  done,
  current,
  onToggle,
  onGo,
}: {
  step: OnboardingStep;
  done: boolean;
  current: boolean;
  onToggle: () => void;
  onGo: () => void;
}) {
  return (
    <li className={`re-onb-step${done ? " done" : ""}${current ? " current" : ""}`}>
      <button
        type="button"
        className={`re-onb-check${done ? " on" : ""}`}
        aria-pressed={done}
        aria-label={done ? `Mark ${step.title} not done` : `Mark ${step.title} done`}
        onClick={onToggle}
      >
        {done ? <Check size={13} strokeWidth={3} /> : null}
      </button>
      <div className="re-onb-step-body">
        <div className="re-onb-step-title">
          {step.title}
          {step.optional && !step.oneOf ? <span className="re-onb-badge">Optional</span> : null}
          {step.oneOf ? <span className="re-onb-badge group">Do one</span> : null}
        </div>
        <p>{step.detail}</p>
        <button type="button" className="re-onb-go" onClick={onGo}>
          Open this page
        </button>
      </div>
    </li>
  );
}
